// src/pages/landing/landingPrelude.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { createPortal } from "react-dom";

/* ---------------- ROUTE -> SECTION MAP ---------------- */

const PATH_TO_SECTION = {
  "/": "top",
  "/top": "top",
  "/concept": "concept",
  "/tracks": "tracks",
  "/veterans": "veterans",
  "/workforce": "workforce",
  "/economic-opportunity": "economic-opportunity",
  "/federal-partners": "federal-partners",
  "/security-compliance": "security-compliance",
  "/community-impact": "community-impact",
  "/plans": "plans",
  "/marketplace": "marketplace",
  "/donations": "donations",
  "/donation-intake": "donation-intake",
  "/contribution": "contribution",
  "/fundraising": "fundraising",
  "/payments": "payments",
  "/calculator": "calculator",
  "/events": "events",
  "/outreach": "outreach",
  "/volunteer": "volunteer",
  "/final-arrangements": "final-arrangements",
  "/legal-listings": "legal-listings",
  "/partners": "partners",
  "/about-us": "about-us",
  "/registration": "registration",
"/concepts": "concept",
  "/track": "tracks",
  "/contributions": "contribution",
  "/fundraisings": "fundraising",
  "/partner": "partners",
  "/partner/": "partners",
  "/about": "about-us",
  "/about-us/": "about-us",

};

const sectionToPath = (id) => {
  if (!id || id === "top") return "/";
  return `/${id}`;
};

/* ---------------- REUSABLE: SPINNING GLOBE ---------------- */
function Globe({ className = "" }) {
  return (
    <span
      className={`inline-block globe-letter globe-spin-continuous align-middle ${className}`}
      aria-hidden="true"
    >
      🌎
    </span>
  );
}

/* ---------------- MOBILE-SAFE DROPDOWN (NO <select>) ---------------- */
function Dropdown({
  value,
  onChange,
  options,
  placeholder = "Select…",
  className = "",
  panelClassName = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef(null);

  const label =
    options.find((o) => o.value === value)?.label ?? (value ? String(value) : "");

  const compute = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();
    const gap = 8;
    setCoords({
      top: Math.round(r.bottom + gap),
      left: Math.round(r.left),
      width: Math.round(r.width),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    compute();

    const onWin = () => compute();
    window.addEventListener("resize", onWin);
    window.addEventListener("scroll", onWin, true);
    return () => {
      window.removeEventListener("resize", onWin);
      window.removeEventListener("scroll", onWin, true);
    };
  }, [open, compute]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Click outside closes
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const btn = btnRef.current;
      if (btn && btn.contains(e.target)) return;

      // panel is portaled; look for it by class
      const panel = document.querySelector(".dropdown-panel");
      if (panel && panel.contains(e.target)) return;

      setOpen(false);
    };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("touchstart", onDown, true);
    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("touchstart", onDown, true);
    };
  }, [open]);

  const panel = open ? (
    <AnimatePresence>
      <motion.div
        className={`dropdown-panel fixed z-[9999] rounded-2xl border border-sky-300/30 shadow-2xl backdrop-blur-xl p-1 ${panelClassName}`}
        style={{
          top: coords.top,
          left: coords.left,
          width: coords.width,
          backgroundColor: "rgba(1, 9, 22, 0.98)",
        }}
        initial={{ opacity: 0, y: -6, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -6, scale: 0.985 }}
        transition={{ duration: 0.14 }}
      >
        <div className="max-h-64 overflow-auto rounded-xl">
          {options.map((o) => {
            const active = o.value === value;
            const isDisabled = !!o.disabled;

            if (isDisabled) {
              return (
                <div
                  key={String(o.value)}
                  className="w-full px-3 py-2 text-left text-xs tracking-wider text-slate-400/90"
                >
                  {o.label}
                </div>
              );
            }

            return (
              <button
                key={String(o.value)}
                type="button"
                className={`w-full text-left px-3 py-2 rounded-xl text-sm transition ${active
                  ? "bg-sky-400/25 text-white"
                  : "text-slate-200 hover:bg-white/10"
                  }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange?.(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </motion.div>
    </AnimatePresence>
  ) : null;

  return (
    <div className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-3 rounded-2xl border border-sky-300/30 bg-[#010916]/80 px-4 py-3 text-left text-sm text-slate-100 shadow-[0_12px_40px_rgba(0,0,0,.35)] backdrop-blur-xl ${disabled ? "opacity-60 cursor-not-allowed" : "hover:bg-[#010916]/90"
          }`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`${label ? "" : "text-slate-400"}`}>
          {label || placeholder}
        </span>
        <span className="text-slate-300">▾</span>
      </button>

      {typeof document !== "undefined" ? createPortal(panel, document.body) : null}
    </div>
  );
}




export { PATH_TO_SECTION, sectionToPath, Globe, Dropdown };
